<template>
  <div class="flex flex-1 min-h-0">
    <FlotoCrudContainer
      as-table
      prepend-new-item
      :disabled="disabled"
      :fetch-fn="getQuntities"
      :delete-fn="deleteQuantity"
      :create-fn="addQuantity"
      :update-fn="updateQuantity"
      :columns="tableColumns"
      :scrollable="false"
      :show-no-data="false"
      :selectable="!disabled"
    >
      <template v-slot:add-controls="{ create, refreshList }">
        <MRow class="mb-4" :gutter="0">
          <MCol :size="12" class="m-align-button">
            <span class="mx-1">
              {{ $tc('available') }} {{ $tc('quantity') }}:
              {{ resource.availableQuantity }}
            </span>
            <AllocationDrawer
              v-if="!disabled"
              :resource="resource"
              :disabled="disabled"
              class="mx-1"
              @refresh="$emit('refresh', $event)"
            />
            <MButton
              v-if="!disabled"
              variant="primary"
              class="mx-1"
              @click="create"
            >
              {{ $t('add') }} {{ $tc('quantity') }}
            </MButton>
          </MCol>
        </MRow>
      </template>
      <template v-slot:form-header="{ item }">
        {{ $t(item.id ? 'edit' : 'add') }} {{ $tc('quantity') }}
      </template>
      <template v-slot:form-items="{ item: productType }">
        <QuantityForm :form-data="productType" />
      </template>
      <template v-slot:form-actions="{ submit, cancel, item, processing }">
        <MButton
          id="update-add-btn"
          class="mx-1"
          :loading="processing"
          @click="submit"
        >
          {{ $t(item.id ? 'update' : 'add') }}
        </MButton>
        <MButton id="cancel-btn" variant="default" @click="cancel">
          {{ $t('cancel') }}
        </MButton>
      </template>
      <template v-if="!disabled" v-slot:bulk-actions="slotData">
        <BulkActions
          v-bind="slotData"
          :bulk-delete-fn="bulkDelete"
          @refresh="$emit('refresh')"
        />
      </template>
      <template v-slot:quantity="{ item, edit }">
        <td class="resource-link">
          <a @click="edit">
            {{ item.quantity }}
          </a>
        </td>
      </template>
      <template v-slot:purchaseCost="{ item }">
        <td class="text-ellipsis">
          <span v-if="item.purchaseCost">
            {{ `${item.purchaseCost}` }}
            <CurrencyPicker :value="item.currencyId" text-only disabled />
          </span>
          <span v-else> --- </span>
        </td>
      </template>
      <template v-slot:purchaseDate="{ item }">
        <td class="text-ellipsis">
          {{ item.purchaseDate | datetime }}
        </td>
      </template>
      <template v-slot:expiryDate="{ item }">
        <td class="text-ellipsis">
          {{ item.expiryDate | datetime(undefined, false) }}
        </td>
      </template>
      <template v-slot:vendorId="{ item }">
        <td class="text-ellipsis">
          {{ vendorName(item) }}
        </td>
      </template>
      <template v-slot:barCode="{ item }">
        <td class="text-ellipsis">
          {{ item.barCode || '---' }}
        </td>
      </template>
      <template v-slot:createdAt="{ item }">
        <td class="text-ellipsis">
          {{ item.createdAt | datetime }}
        </td>
      </template>
      <template v-slot:createdBy="{ item }">
        <td class="text-ellipsis">
          <FlotoTechnicianPicker :value="item.createdBy" disabled />
        </td>
      </template>
      <template v-slot:actions="{ item, remove, edit }">
        <td class="text-ellipsis">
          <MTooltip>
            <template v-slot:trigger>
              <a :disabled="disabled" @click="edit">
                <MIcon name="pencil" class="cursor-pointer text-primary" />
              </a>
            </template>
            {{ $t('edit') }}
          </MTooltip>

          <FlotoDeleteBtn
            class="mx-2"
            :disabled="disabled"
            :class="{ invisible: !item.canDelete }"
            :message="$t('confirm_delete_item', { item: $tc('quantity') })"
            @confirm="remove"
          />
        </td>
      </template>
    </FlotoCrudContainer>
  </div>
</template>

<script>
import CurrencyPicker from '@components/data-picker/currency-picker'
import { getVendorCatalogsApi } from '@modules/asset-management/api/vendor-catalog-api'
import QuantityForm from './quantity-form'
import BulkActions from './bulk-actions.vue'
import AllocationDrawer from '../allocation-container/allocation-drawer.vue'
import {
  getQuantitiesApi,
  bulkDeleteApi,
  updateQuantityApi,
  createQuantityApi,
} from '../../api/quantity-api'
export default {
  name: 'QuantityList',
  components: { QuantityForm, CurrencyPicker, BulkActions, AllocationDrawer },
  props: {
    resourceId: { type: Number, required: true },
    resource: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      searchTerm: '',
      tableColumns: [
        { name: `${this.$t('quantity')}`, key: 'quantity' },
        {
          name: `${this.$tc('purchase')} ${this.$tc('cost')}`,
          key: 'purchaseCost',
        },
        {
          name: `${this.$tc('purchase')} ${this.$tc('date')}`,
          key: 'purchaseDate',
        },
        {
          name: `${this.$tc('expiry')} ${this.$tc('date')}`,
          key: 'expiryDate',
        },
        {
          name: `${this.$tc('vendor')}`,
          key: 'vendorId',
        },
        {
          name: `${this.$tc('barcode')}`,
          key: 'barCode',
        },
        {
          name: `${this.$tc('created_time')}`,
          key: 'createdAt',
        },
        {
          name: `${this.$tc('created_by')}`,
          key: 'createdBy',
        },
        {
          name: `${this.$tc('action', 2)}`,
          key: 'actions',
          width: '120px',
        },
      ],
      vendorList: [],
    }
  },
  methods: {
    getQuntities(limit, offset) {
      return getQuantitiesApi(this.resourceId, limit, offset).then(
        (response) => {
          const vendorIds = response.items.map((i) => i.vendorId)
          if (vendorIds.length) {
            this.getVendors(vendorIds)
          }
          return response
        }
      )
    },
    deleteQuantity({ id }) {
      return bulkDeleteApi(this.resourceId, [id]).then((data) => {
        this.$emit('refresh', ['consumable_property'])
        return data
      })
    },
    bulkDelete(ids) {
      return bulkDeleteApi(this.resourceId, ids).then((data) => {
        this.$emit('refresh', ['consumable_property'])
        return data
      })
    },
    updateQuantity(data) {
      return updateQuantityApi(this.resourceId, data).then((data) => {
        this.$emit('refresh', ['consumable_property'])
        return data
      })
    },
    addQuantity(data) {
      const requestData = { ...data }
      return createQuantityApi(this.resourceId, requestData).then((data) => {
        this.$emit('refresh', ['consumable_property'])
        return data
      })
    },
    getVendors(ids) {
      getVendorCatalogsApi({ ids }).then((data) => {
        this.vendorList = data.items
      })
    },
    vendorName(item) {
      if (item.vendorId) {
        const vendor = this.vendorList.find((v) => v.id === item.vendorId)
        return vendor ? `${vendor.name}: ${vendor.displayName}` : '---'
      }
      return '---'
    },
  },
}
</script>
